import { Component, ReactNode, useMemo, useState } from 'react'
import { Document, Page } from 'react-pdf'
import { useTranslation } from 'react-i18next'
import { Spinner } from './spinner'
import { FaFileExcel } from 'react-icons/fa'

interface PdfPreviewProps {
  file: string
  /**
   * Stable identity for the file (e.g. the receipt id). Presigned S3 URLs get a
   * fresh signature on every list refetch; without a stable key react-pdf tears
   * down and reloads the document, racing a pending `Page.getPage()` against the
   * now-destroyed document ("Cannot read properties of null (reading
   * 'sendWithPromise')"). Keying on the id freezes the URL so no reload happens.
   */
  cacheKey?: string
  /** Render every page instead of only the first (used for full previews). */
  allPages?: boolean
  width?: number
  className?: string
  pageClassName?: string
}

/**
 * Contains react-pdf render-phase failures so a single bad document can never
 * crash the surrounding page. Resets whenever the previewed file changes.
 */
class PdfErrorBoundary extends Component<
  { resetKey: string; fallback: ReactNode; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidUpdate(prevProps: { resetKey: string }) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false })
    }
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children
  }
}

export const PdfPreview = ({
  file,
  cacheKey,
  allPages = false,
  width,
  className,
  pageClassName,
}: PdfPreviewProps) => {
  const { t } = useTranslation()
  const [numPages, setNumPages] = useState<number | null>(null)
  const key = cacheKey ?? file
  // Freeze the URL to its first value for a given key so presigned-URL churn on
  // refetches doesn't reload (and destroy) the document mid-render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableFile = useMemo(() => file, [key])

  const errorFallback = (
    <div className="flex flex-col items-center justify-center text-center text-error">
      <FaFileExcel className="mb-2 h-8 w-8" />
      <span className="text-xs">{t('receipts.failedToLoad')}</span>
    </div>
  )

  return (
    <PdfErrorBoundary resetKey={key} fallback={errorFallback}>
      <Document
        file={stableFile}
        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
        loading={<Spinner size="sm" />}
        error={errorFallback}
        className={className}
      >
        {allPages ? (
          // Only render pages once the document has loaded, so getPage() is never
          // called against a document that is still being torn down.
          numPages ? (
            Array.from(new Array(numPages), (_, index) => (
              <Page
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                width={width}
                className={pageClassName}
              />
            ))
          ) : null
        ) : (
          <Page pageNumber={1} width={width} className={pageClassName} />
        )}
      </Document>
    </PdfErrorBoundary>
  )
}
